// src/merch/PrintAreaEditor.jsx
//
// Flat view of the printable sticker: product colour background, the
// composited design cropped to the print area, the cut line (dieline) and
// a dashed safe zone.
//
// On-canvas editing:
//   • tap a layer to select it (topmost hit wins), tap empty space to deselect
//   • with a layer selected, drag ANYWHERE to move it (Photoshop-style) —
//     small layers don't need a precise grab; handles only win when touched
//     outside the layer's box
//   • nudge buttons for fine moves / rotate / scale; the 3D view follows live
//   • corner handles = uniform scale, top knob = rotate (snaps to 45°,
//     Shift = 15° steps)
//   • two-finger pinch/twist that starts on the selected layer = scale +
//     rotate + move it; two fingers anywhere else = zoom / pan the view
//     (sketch-app style, see "view navigation" below)
//   • keyboard: arrows nudge 0.2mm (Shift 1mm), Delete removes, Esc deselects
// In Draw mode the handles hide and pointer input paints instead.

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { merch, updateTransform, selectLayer, removeLayer, duplicateLayer, linkedWith } from "./store";
import { getPaintCanvas, beginStroke, extendStroke, endStroke, cancelStroke, undoStroke, fillAt, PAINT_SIZE } from "./paint";
import { stickerPath2D } from "./shapes";
import { snapshot } from "valtio";
import { buildFlattenedUV } from "../engine";
import { liveCanvas, publishLive, LIVE_SIZE } from "./liveTexture";
import { history, undo, redo } from "./history";

/** Draw one layer straight onto the editor canvas at its live transform
 *  (same maths as the engine: contain in the unit square, then
 *  translate · rotate · skew · scale · flip about the centre). */
function drawLiveLayer(ctx, L, img, area, k) {
  const t = L.transform || {};
  const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;
  ctx.save();
  ctx.globalAlpha = L.opacity ?? 1;
  const b = (L.blend || "block").toLowerCase();
  if (b === "ink" || b === "skin" || b === "multiply") ctx.globalCompositeOperation = "multiply";
  ctx.scale(k, k);
  ctx.translate(-area.x0, -area.y0);
  ctx.translate(t.tx ?? 0.5, t.ty ?? 0.5);
  ctx.rotate(t.rot || 0);
  if (t.skewX || t.skewY) ctx.transform(1, Math.tan(t.skewY || 0), Math.tan(t.skewX || 0), 1, 0, 0);
  const sx = typeof t.scaleX === "number" ? t.scaleX : t.scale || 1;
  const sy = typeof t.scaleY === "number" ? t.scaleY : t.scale || 1;
  ctx.scale(sx, sy);
  if (t.flipX) ctx.scale(-1, 1);
  ctx.translate(-0.5, -0.5);
  const m = Math.max(iw, ih), w = iw / m, h = ih / m;
  if (b === "whitefill") {
    // solid white silhouette of the image (same as the engine's white pass)
    const c = whiteOf(img, iw, ih);
    ctx.drawImage(c, (1 - w) / 2, (1 - h) / 2, w, h);
  } else {
    ctx.drawImage(img, (1 - w) / 2, (1 - h) / 2, w, h);
  }
  ctx.restore();
}

const whiteCache = new WeakMap();
function whiteOf(img, iw, ih) {
  let c = whiteCache.get(img);
  if (!c) {
    c = document.createElement("canvas");
    c.width = iw; c.height = ih;
    const x = c.getContext("2d");
    x.drawImage(img, 0, 0);
    x.globalCompositeOperation = "source-in";
    x.fillStyle = "#fff";
    x.fillRect(0, 0, iw, ih);
    whiteCache.set(img, c);
  }
  return c;
}

const SAFE_INSET = 0.06; // fraction of the short edge
const HANDLE_R = 8;      // visible handle radius (px)
const HIT_R = 22;        // touch-friendly hit radius (px)
const KNOB_GAP = 28;     // rotate knob distance above the box (px)

/* ---------- natural image size cache (for selection boxes) ---------- */
const dimCache = new Map(); // url -> {w,h} | "loading"
function useLayerDims(layers) {
  const [, force] = useState(0);
  useEffect(() => {
    let alive = true;
    // forget images no layer uses any more (recolouring the jersey makes a
    // new image each time; don't keep the old ones decoded)
    const inUse = new Set();
    for (const arr of [merch.layers, ...Object.values(merch.sideLayers || {})]) for (const L of arr || []) inUse.add(L.url);
    for (const url of dimCache.keys()) if (!inUse.has(url)) dimCache.delete(url);
    for (const L of layers) {
      if (!L.url || dimCache.has(L.url)) continue;
      dimCache.set(L.url, "loading");
      const img = new Image();
      img.onload = () => {
        dimCache.set(L.url, { w: img.naturalWidth || 1, h: img.naturalHeight || 1, img });
        if (alive) force((n) => n + 1);
      };
      img.onerror = () => dimCache.delete(L.url);
      img.src = L.url;
    }
    return () => { alive = false; };
  }, [layers]);
  return (url) => {
    const d = dimCache.get(url);
    return d && d !== "loading" ? d : null;
  };
}

/** Half-extents of a layer in design units (0–1 square), matching the
 *  engine: image is contained in the square, then scaled. */
function halfExtents(t, d) {
  const m = Math.max(d.w, d.h);
  const sx = typeof t.scaleX === "number" ? t.scaleX : t.scale || 1;
  const sy = typeof t.scaleY === "number" ? t.scaleY : t.scale || 1;
  return [(d.w / m / 2) * sx, (d.h / m / 2) * sy];
}

export default function PrintAreaEditor({ area, sticker, colour, composite, selectedId, paintActive, fill = false, guides = [] }) {
  const snap = useSnapshot(merch);
  // read here (not passed down from the app) so a paint frame only
  // re-renders the editor, not the whole page + 3D view
  const paintRev = snap.paint.rev;
  const hist = useSnapshot(history);
  const wrapRef = useRef(null);
  const scrollRef = useRef(null);
  const [scrollX, setScrollX] = useState(0);
  // zoom for detailed placement (the editor scrolls when zoomed in)
  const [zoom, setZoom] = useState(1);
  const [showGuides, setShowGuides] = useState(true); // non-printing colouring guides
  const [showSafe, setShowSafe] = useState(true);     // blue "keep text out" edge band
  // live dragging: while a layer moves, draw the OTHER layers once (dragBase)
  // and the moving layer directly on top — no waiting for a full re-composite
  const [dragBase, setDragBase] = useState(null); // { id, canvas }
  const liveSel = snap.layers.find((l) => l.id === selectedId) || null;
  // the selected layer plus anything linked to it, bottom first (they all move together)
  const liveGroup = liveSel ? linkedWith(liveSel.id, snap.layers).slice().reverse() : [];
  const liveKey = dragBase && liveSel && dragBase.id === liveSel.id ? JSON.stringify(liveGroup.map((l) => l.transform)) : null;
  /* ---------- view navigation (sketch-app style) ----------
     • two fingers anywhere = pinch-zoom + pan the VIEW (a quick two-finger
       tap = undo); two fingers starting on the selected layer still
       scale/rotate that layer
     • one finger on empty space pans when zoomed in
     • drawing with a pencil: fingers only navigate, never paint
     • mouse: ⌘/Ctrl + wheel or trackpad pinch zooms at the cursor, wheel /
       two-finger scroll pans, Space + drag or middle-drag pans          */
  const ZMAX = 12; // deep zoom for fine work (a tooth with a tiny brush)
  const zoomRef = useRef(1);   // latest zoom (wheel events arrive faster than renders)
  zoomRef.current = zoom;
  const anchor = useRef(null); // { ax, ay, fx, fy }: keep this canvas spot under this screen point
  const canvasWrapRef = useRef(null);
  const cpts = useRef(new Map()); // pointerId → [clientX, clientY, pointerType]
  const sawPen = useRef(false);
  const spaceDown = useRef(false);
  const hovering = useRef(false); // Space only pans while the mouse is over the design
  const clampZ = (z) => Math.min(ZMAX, Math.max(1, z));
  function zoomAt(next, clientX, clientY) {
    next = clampZ(next);
    if (next < 1.04) next = 1;
    const cv = canvasWrapRef.current, sc = scrollRef.current;
    if (!cv || !sc || Math.abs(next - zoomRef.current) < 0.001) return false;
    const r = cv.getBoundingClientRect();
    const sr = sc.getBoundingClientRect();
    if (clientX == null) { clientX = sr.left + sr.width / 2; clientY = sr.top + sr.height / 2; }
    anchor.current = { ax: clientX, ay: clientY, fx: (clientX - r.left) / r.width, fy: (clientY - r.top) / r.height };
    zoomRef.current = next;
    setZoom(next);
    return true;
  }
  function stepZoom(dir) {
    const z = zoomRef.current;
    zoomAt(dir > 0 ? z * 1.5 : z / 1.5);
  }
  function clearNavTransform() {
    const w = canvasWrapRef.current;
    if (w) w.style.transform = "";
    wrapRef.current?.classList.remove("navigating");
  }
  // ⌘/Ctrl + wheel and trackpad pinch zoom at the cursor (smoothly)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const d = Math.max(-60, Math.min(60, e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY));
      zoomAt(zoomRef.current * Math.exp(-d * 0.01), e.clientX, e.clientY);
    };
    // desktop Safari sends trackpad pinches as gesture events
    let g0 = 1;
    const desktop = (navigator.maxTouchPoints || 0) === 0;
    const onGS = (e) => { e.preventDefault(); g0 = zoomRef.current; };
    const onGC = (e) => { e.preventDefault(); zoomAt(g0 * e.scale, e.clientX, e.clientY); };
    el.addEventListener("wheel", onWheel, { passive: false });
    if (desktop) { el.addEventListener("gesturestart", onGS); el.addEventListener("gesturechange", onGC); }
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("gesturestart", onGS);
      el.removeEventListener("gesturechange", onGC);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Space + drag pans (like Photoshop / Procreate on desktop)
  useEffect(() => {
    const typing = (e) => /^(input|textarea|select)$/i.test(e.target?.tagName || "") || e.target?.isContentEditable;
    const down = (e) => {
      if (e.code !== "Space" || typing(e) || !hovering.current) return;
      e.preventDefault();
      if (!spaceDown.current) { spaceDown.current = true; wrapRef.current?.classList.add("space-pan"); }
    };
    const up = (e) => {
      if (e.code !== "Space") return;
      spaceDown.current = false;
      wrapRef.current?.classList.remove("space-pan");
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);
  const canvasRef = useRef(null);
  const [viewTick, setViewTick] = useState(0); // redraw the visible area after scrolling
  // ...and whenever the pane itself changes size/padding (zoom class flips,
  // long-strip mode, rotation), so the drawn area never goes stale
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc || typeof ResizeObserver === "undefined") return;
    let raf = 0;
    const ro = new ResizeObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setViewTick((n) => n + 1)); });
    ro.observe(sc);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
  }, []);
  const scrollRaf = useRef(0);
  // zoomed in: a sharper composite of the layers (the shared one is 1024)
  const [hiRes, setHiRes] = useState(null);
  useEffect(() => {
    if (zoom < 2.5 || snap.dragging) return;
    let alive = true;
    const t = setTimeout(async () => {
      const layers = snapshot(merch).layers;
      const c = await buildFlattenedUV(layers, { size: 3072, edgeClip: 0.05 });
      if (alive) setHiRes(c);
    }, 150);
    return () => { alive = false; clearTimeout(t); };
  }, [zoom >= 2.5, snap.rev, snap.side, snap.dragging]); // eslint-disable-line react-hooks/exhaustive-deps
  const gesture = useRef(null);       // active move/scale/rotate/pinch/paint
  const pts = useRef(new Map());       // live pointers (for pinch)
  const [view, setView] = useState({ W: 0, H: 0 });
  const [readout, setReadout] = useState(null);
  const [resizeTick, setResizeTick] = useState(0);
  const dimsOf = useLayerDims(snap.layers);

  const aw = area.x1 - area.x0;
  const ah = area.y1 - area.y0;
  const aspect = aw / ah;
  const k = view.W ? view.W / aw : 1; // px per design unit

  // re-fit when the container changes size (window resize, rotation,
  // entering/leaving full screen)
  useEffect(() => {
    const onR = () => setResizeTick((n) => n + 1);
    window.addEventListener("resize", onR);
    const ro = typeof ResizeObserver !== "undefined" && wrapRef.current ? new ResizeObserver(onR) : null;
    ro?.observe(wrapRef.current);
    return () => { window.removeEventListener("resize", onR); ro?.disconnect(); };
  }, []);

  /* ---------- draw ---------- */
  // layout effect: resize + re-anchor before the browser paints (no jump after a pinch)
  useLayoutEffect(() => {
    const cv = canvasRef.current;
    const wrap = wrapRef.current;
    if (!cv || !wrap) return;
    // fill: fit the whole container (full-screen editor), leaving room for
    // the handles; otherwise a card below the 3D view
    const maxW = wrap.clientWidth - (fill ? 56 : 32) - 24; // 24 = the pane's own padding
    const phone = window.innerWidth <= 860;
    const maxH = fill ? wrap.clientHeight - 104 : phone ? Math.min(240, window.innerHeight * 0.28) : Math.min(520, window.innerHeight * 0.5);
    if (maxW <= 0 || maxH <= 0) return;
    let W = maxW, H = maxW / aspect;
    if (H > maxH) { H = maxH; W = H * aspect; }
    // long strips (shaft stickers): keep a usable height and scroll sideways
    const minH = fill ? 120 : phone ? 90 : 120;
    if (H < minH && aspect > 4) { H = Math.min(minH, maxH); W = H * aspect; }
    const baseH = Math.round(H);
    W = Math.round(W * zoom); H = Math.round(H * zoom);
    // the wrapper is the full (zoomed) sticker size, so the page scrolls over it
    const wrapEl = canvasWrapRef.current, sc = scrollRef.current;
    wrapEl.style.width = `${W}px`;
    wrapEl.style.height = `${H}px`;
    setView((v) => (v.W === W && v.H === H && v.baseH === baseH ? v : { W, H, baseH }));
    // after a zoom, keep the same spot under the fingers / cursor
    if (anchor.current && sc) {
      const a = anchor.current;
      anchor.current = null;
      clearNavTransform();
      const r = wrapEl.getBoundingClientRect();
      sc.scrollLeft += r.left - (a.ax - a.fx * W);
      sc.scrollTop += r.top - (a.ay - a.fy * H);
      setScrollX(sc.scrollLeft);
    }
    // ...but the canvas only covers what's on screen (plus a margin for
    // pinching), so deep zoom stays sharp and fast
    let vx = 0, vy = 0, vw = W, vh = H;
    if (sc && zoom > 1) {
      const ox = wrapEl.offsetLeft - sc.scrollLeft, oy = wrapEl.offsetTop - sc.scrollTop; // wrap's spot in the viewport
      const mx = sc.clientWidth * 0.5, my = sc.clientHeight * 0.5;
      const x0 = Math.max(0, -ox - mx), y0 = Math.max(0, -oy - my);
      const x1 = Math.min(W, sc.clientWidth - ox + mx), y1 = Math.min(H, sc.clientHeight - oy + my);
      if (x1 > x0 && y1 > y0) { vx = Math.floor(x0); vy = Math.floor(y0); vw = Math.ceil(x1 - vx); vh = Math.ceil(y1 - vy); }
    }
    // keep the backing canvas under ~16 MP (iPad Safari's limit)
    const dpr = Math.min(window.devicePixelRatio || 1, Math.sqrt(16e6 / Math.max(1, vw * vh)));
    const bw = Math.round(vw * dpr), bh = Math.round(vh * dpr);
    if (cv.width !== bw) cv.width = bw;
    if (cv.height !== bh) cv.height = bh;
    cv.style.left = `${vx}px`;
    cv.style.top = `${vy}px`;
    cv.style.width = `${vw}px`;
    cv.style.height = `${vh}px`;
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, -vx * dpr, -vy * dpr);
    ctx.clearRect(0, 0, W, H);

    const pad = 1.5;
    const shape = stickerPath2D(sticker, pad, pad, W - pad * 2, H - pad * 2);

    // product colour (what's under a clear sticker) + design
    ctx.save();
    if (sticker.contour) {
      // die-cut: backing paper, the vinyl in its cut shape, and the art
      // NOT clipped to the cut (the cut line catches up a moment later)
      ctx.fillStyle = "#e7e3dc";
      ctx.fillRect(0, 0, W, H);
      ctx.shadowColor = "rgba(0,0,0,0.18)"; ctx.shadowBlur = 6; ctx.shadowOffsetY = 1.5;
      ctx.fillStyle = colour.hex;
      ctx.fill(shape);
      ctx.shadowColor = "transparent";
    } else {
      ctx.clip(shape);
      ctx.fillStyle = colour.hex;
      ctx.fillRect(0, 0, W, H);
    }
    const liveImgs = liveKey ? liveGroup.map((l) => dimCache.get(l.url)?.img) : null;
    const liveImg = liveImgs && liveImgs.every(Boolean) ? liveImgs : null;
    const base = liveImg ? dragBase.canvas : (zoom >= 2.5 && hiRes) || composite;
    if (base) {
      const cw = base.width;
      ctx.drawImage(base, area.x0 * cw, area.y0 * cw, aw * cw, ah * cw, 0, 0, W, H);
    }
    if (liveImg) {
      liveGroup.forEach((l, i) => l.visible !== false && drawLiveLayer(ctx, l, liveImg[i], area, W / aw));
      // same frame for the 3D view (full design square)
      const lc = liveCanvas();
      const lctx = lc.getContext("2d");
      lctx.clearRect(0, 0, LIVE_SIZE, LIVE_SIZE);
      lctx.drawImage(dragBase.canvas, 0, 0, LIVE_SIZE, LIVE_SIZE);
      liveGroup.forEach((l, i) => l.visible !== false && drawLiveLayer(lctx, l, liveImg[i], { x0: 0, y0: 0 }, LIVE_SIZE));
      publishLive(merch.side, lc);
    }
    // colouring guides (never printed): light dashed lines in sticker mm
    if (showGuides && guides?.length) {
      const gx = (W - pad * 2) / sticker.widthMm, gy = (H - pad * 2) / sticker.heightMm;
      ctx.save();
      ctx.strokeStyle = "rgba(90,90,90,0.55)";
      ctx.lineWidth = 1.2;
      ctx.lineJoin = "round";
      for (const g of guides) {
        ctx.setLineDash(g.dot ? [1.5, 3.5] : [5, 4]);
        ctx.beginPath();
        g.pts.forEach(([x, y], i) => {
          const X = pad + x * gx, Y = pad + y * gy;
          if (i) ctx.lineTo(X, Y); else ctx.moveTo(X, Y);
        });
        if (g.closed) ctx.closePath();
        ctx.stroke();
      }
      ctx.restore();
    }
    // live paint buffer (not yet committed as a layer)
    const paint = getPaintCanvas();
    if (paint) {
      ctx.drawImage(paint, area.x0 * PAINT_SIZE, area.y0 * PAINT_SIZE, aw * PAINT_SIZE, ah * PAINT_SIZE, 0, 0, W, H);
    }
    ctx.restore();

    // die-cut: dashed box = the biggest the sticker can be; blue band = keep
    // art out (its border would run past the edge)
    if (sticker.contour) {
      const kx = (W - pad * 2) / sticker.widthMm;
      const band = (merch.contour?.offsetMm ?? 3.175) * kx;
      ctx.save();
      if (showSafe) {
        ctx.fillStyle = "rgba(60,120,220,0.10)";
        ctx.beginPath();
        ctx.rect(pad, pad, W - pad * 2, H - pad * 2);
        ctx.rect(pad + band, pad + band, W - pad * 2 - band * 2, H - pad * 2 - band * 2);
        ctx.fill("evenodd");
      }
      ctx.setLineDash([6, 5]);
      ctx.strokeStyle = "rgba(90,90,90,0.6)";
      ctx.lineWidth = 1;
      ctx.strokeRect(pad, pad, W - pad * 2, H - pad * 2);
      ctx.restore();
    }
    // safe zone: a soft band just inside the cut line (works for any shape)
    const inset = Math.min(W, H) * SAFE_INSET;
    if (showSafe && !sticker.contour) {
    ctx.save();
    ctx.clip(shape);
    ctx.strokeStyle = "rgba(60,120,220,0.16)";
    ctx.lineWidth = inset * 2;
    ctx.stroke(shape);
    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = "rgba(60,120,220,0.55)";
    ctx.lineWidth = 1;
    ctx.restore();
    }

    // cut line
    ctx.save();
    ctx.strokeStyle = "#e0218a"; // magenta = conventional cut-line colour
    ctx.lineWidth = 1.5;
    ctx.stroke(shape);
    ctx.restore();
  }, [composite, area, aspect, aw, ah, colour, sticker, paintRev, resizeTick, fill, zoom, liveKey, dragBase, showGuides, showSafe, guides, viewTick, hiRes]); // eslint-disable-line react-hooks/exhaustive-deps

  // never leave compositing paused if the editor unmounts mid-drag
  useEffect(() => () => { if (merch.dragging) { merch.dragging = false; merch.rev++; } }, []);

  function beginLive(id) {
    merch.dragging = true;
    const moving = new Set(linkedWith(id).map((l) => l.id));
    const others = snapshot(merch).layers.filter((l) => !moving.has(l.id));
    buildFlattenedUV(others, { size: LIVE_SIZE, edgeClip: 0.05 }).then((c) => {
      const paint = getPaintCanvas();
      if (paint) c.getContext("2d").drawImage(paint, 0, 0, c.width, c.height);
      if (merch.dragging) setDragBase({ id, canvas: c });
    });
  }
  function endLive() {
    if (!merch.dragging) return;
    merch.dragging = false;
    setDragBase(null);
    merch.rev++; // now rebuild the real composite (and the 3D view)
  }

  /* ---------- coordinate helpers ---------- */
  function localPx(e) {
    const r = canvasWrapRef.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  }
  const pxToUnit = ([x, y]) => [area.x0 + x / k, area.y0 + y / k];
  const unitToPx = (u, v) => [(u - area.x0) * k, (v - area.y0) * k];

  function toPaintPx(e) {
    const [u, v] = pxToUnit(localPx(e));
    return [u * PAINT_SIZE, v * PAINT_SIZE];
  }

  /** Topmost visible layer whose (rotated) box contains the point. */
  function hitTest(u, v) {
    for (const L of merch.layers) {
      if (!L.visible || !L.url || L.locked) continue; // locked = jersey art: never grabbed
      const d = dimsOf(L.url);
      if (!d) continue;
      const t = L.transform;
      const [hx, hy] = halfExtents(t, d);
      const dx = u - (t.tx ?? 0.5), dy = v - (t.ty ?? 0.5);
      const c = Math.cos(-(t.rot || 0)), s = Math.sin(-(t.rot || 0));
      const lx = dx * c - dy * s, ly = dx * s + dy * c;
      if (Math.abs(lx) <= hx && Math.abs(ly) <= hy) return L.id;
    }
    return null;
  }

  const selL = () => merch.layers.find((l) => l.id === merch.selectedId && !l.locked) || null;

  function scaledPatch(t0, f) {
    const clamp = (x) => Math.min(3, Math.max(0.02, x));
    const p = { scale: clamp((t0.scale || 1) * f) };
    if (typeof t0.scaleX === "number") p.scaleX = clamp(t0.scaleX * f);
    if (typeof t0.scaleY === "number") p.scaleY = clamp(t0.scaleY * f);
    return p;
  }

  function snapRot(r, fine) {
    let deg = (r * 180) / Math.PI;
    deg = ((((deg + 180) % 360) + 360) % 360) - 180;
    if (fine) deg = Math.round(deg / 15) * 15;
    else {
      const n = Math.round(deg / 45) * 45;
      if (Math.abs(deg - n) < 4) deg = n;
    }
    return deg;
  }

  function startPinch() {
    const L = selL();
    if (!L) return (gesture.current = null);
    const [a, b] = [...pts.current.values()];
    gesture.current = {
      mode: "pinch",
      d0: Math.hypot(b[0] - a[0], b[1] - a[1]) || 1,
      a0: Math.atan2(b[1] - a[1], b[0] - a[0]),
      m0: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
      t0: { ...L.transform },
    };
  }

  /* ---------- view navigation gestures ---------- */
  const touchPts = () => [...cpts.current.values()].filter((p) => p[2] === "touch");
  function startNav() {
    const [a, b] = touchPts();
    const w = canvasWrapRef.current;
    if (!a || !b || !w) return (gesture.current = null);
    wrapRef.current?.classList.remove("panning");
    wrapRef.current?.classList.add("navigating");
    w.style.transformOrigin = "0 0";
    gesture.current = {
      mode: "nav",
      d0: Math.hypot(b[0] - a[0], b[1] - a[1]) || 1,
      mid0: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
      r0: w.getBoundingClientRect(),
      z0: zoomRef.current,
      t0: performance.now(),
      s: 1, tx: 0, ty: 0, moved: false,
    };
  }
  function moveNav(g) {
    const [a, b] = touchPts();
    if (!a || !b) return;
    const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const m = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const s = clampZ(g.z0 * (d / g.d0)) / g.z0;
    // keep the content point first under the fingers' midpoint under it now
    const tx = m[0] - g.r0.left - s * (g.mid0[0] - g.r0.left);
    const ty = m[1] - g.r0.top - s * (g.mid0[1] - g.r0.top);
    Object.assign(g, { s, tx, ty, m });
    if (Math.abs(s - 1) > 0.04 || Math.hypot(tx, ty) > 10) g.moved = true;
    canvasWrapRef.current.style.transform = `translate(${tx}px, ${ty}px) scale(${s})`;
  }
  function endNav(g) {
    const sc = scrollRef.current;
    if (!g.moved) {
      clearNavTransform();
      // quick two-finger tap = undo (like Procreate)
      if (performance.now() - g.t0 < 280) {
        if (paintActive) { if (merch.paint.strokes > 0) undoStroke(); }
        else undo();
      }
      return;
    }
    const z = g.z0 * g.s;
    if (Math.abs(g.s - 1) < 0.02 || !zoomAt(z, g.m[0], g.m[1])) {
      // pan only: hand the movement to the scroller
      clearNavTransform();
      if (sc) { sc.scrollLeft -= g.tx; sc.scrollTop -= g.ty; setScrollX(sc.scrollLeft); }
      return;
    }
    // zoomAt measured the canvas WITH the live transform; re-express the
    // anchor in untransformed terms so the redraw lands exactly
    anchor.current.fx = (g.mid0[0] - g.r0.left) / g.r0.width;
    anchor.current.fy = (g.mid0[1] - g.r0.top) / g.r0.height;
  }
  const canPan = () => {
    const sc = scrollRef.current;
    return !!sc && (sc.scrollWidth > sc.clientWidth + 1 || sc.scrollHeight > sc.clientHeight + 1);
  };
  function startPan(e) {
    const sc = scrollRef.current;
    gesture.current = { mode: "pan", id: e.pointerId, c0: [e.clientX, e.clientY], s0: [sc?.scrollLeft || 0, sc?.scrollTop || 0] };
    wrapRef.current?.classList.add("panning");
  }

  /* ---------- pointer handling (one handler set on the wrapper) ---------- */
  function onPointerDown(e) {
    const el = e.currentTarget;
    // clicks on the scroller's own scrollbars stay native
    if (e.target === el && e.pointerType === "mouse" && (e.nativeEvent.offsetX > el.clientWidth || e.nativeEvent.offsetY > el.clientHeight)) return;
    // middle-drag or Space + drag: pan
    if (e.button === 1 || (e.button === 0 && spaceDown.current && e.pointerType === "mouse")) {
      e.preventDefault();
      el.setPointerCapture?.(e.pointerId);
      return startPan(e);
    }
    if (e.button && e.button !== 0) return;
    if (e.pointerType === "pen") sawPen.current = true;
    const touch = e.pointerType === "touch";
    cpts.current.set(e.pointerId, [e.clientX, e.clientY, e.pointerType]);
    el.setPointerCapture?.(e.pointerId);
    const g0 = gesture.current;
    const nTouch = touch ? touchPts().length : 0;

    // a second finger: navigate (unless it's resizing the selected layer)
    if (touch && nTouch === 2) {
      if (g0?.mode === "paint") {
        if (!g0.touch) return; // pencil is drawing: ignore the palm/finger
        if (performance.now() - g0.t0 > 300) return; // a real line: keep it, ignore the extra finger
        cancelStroke();
        return startNav();
      }
      if (!paintActive && g0?.mode === "move" && g0.inside && selL()) {
        pts.current.set(e.pointerId, localPx(e));
        return startPinch();
      }
      if (g0?.mode === "move" && g0.live) {
        updateTransform(g0.id, { tx: g0.tx, ty: g0.ty }); // undo the accidental one-finger move
        endLive();
      }
      pts.current.clear();
      return startNav();
    }
    if (touch && nTouch > 2) return;
    if (g0?.mode === "nav" || g0?.mode === "pan") return;

    const p = localPx(e);

    if (paintActive) {
      // drawing with a pencil: fingers only move the view
      if (touch && sawPen.current) {
        if (canPan()) startPan(e);
        return;
      }
      if (g0?.mode === "paint") return; // ignore extra pointers
      if (merch.paint.tool === "fill" && !merch.paint.erase) {
        e.preventDefault();
        const [x, y] = toPaintPx(e);
        fillAt(x, y, sticker, area);
        return;
      }
      e.preventDefault();
      const [x, y] = toPaintPx(e);
      const paintPerScreen = (aw * PAINT_SIZE) / Math.max(1, canvasWrapRef.current.getBoundingClientRect().width);
      beginStroke(x, y, e, sticker, area, paintPerScreen);
      gesture.current = { mode: "paint", id: e.pointerId, t0: performance.now(), touch };
      return;
    }

    pts.current.set(e.pointerId, p);
    if (pts.current.size > 1) return;

    const handle = e.target?.getAttribute?.("data-handle");
    const L = selL();
    if (L && (handle === "scale" || handle === "rotate")) {
      const t = L.transform;
      const c = unitToPx(t.tx ?? 0.5, t.ty ?? 0.5);
      gesture.current = handle === "scale"
        ? { mode: "scale", c, d0: Math.hypot(p[0] - c[0], p[1] - c[1]) || 1, t0: { ...t } }
        : { mode: "rotate", c, a0: Math.atan2(p[1] - c[1], p[0] - c[0]), r0: t.rot || 0 };
      return;
    }

    const [u, v] = pxToUnit(p);
    const hit = L && handle === "body" ? L.id : hitTest(u, v);
    if (L) {
      // a layer is selected: dragging anywhere moves it. A tap (no drag)
      // on another layer selects that one; a tap on empty space deselects.
      const T = L.transform;
      gesture.current = { mode: "move", id: L.id, p0: p, tx: T.tx ?? 0.5, ty: T.ty ?? 0.5, tap: hit, inside: hit === L.id };
      return;
    }
    if (!hit) {
      // nothing selected, empty space: drag pans the zoomed view
      if (canPan()) startPan(e);
      else gesture.current = null;
      return;
    }
    selectLayer(hit);
    const T = merch.layers.find((l) => l.id === hit).transform;
    gesture.current = { mode: "move", id: hit, p0: p, tx: T.tx ?? 0.5, ty: T.ty ?? 0.5, tap: hit, inside: true };
  }

  function onPointerMove(e) {
    if (cpts.current.has(e.pointerId)) {
      const c = cpts.current.get(e.pointerId);
      cpts.current.set(e.pointerId, [e.clientX, e.clientY, c[2]]);
    }
    const g = gesture.current;
    if (g?.mode === "nav") return moveNav(g);
    if (g?.mode === "pan") {
      if (e.pointerId !== g.id) return;
      const sc = scrollRef.current;
      if (sc) {
        sc.scrollLeft = g.s0[0] - (e.clientX - g.c0[0]);
        sc.scrollTop = g.s0[1] - (e.clientY - g.c0[1]);
      }
      return;
    }
    if (g?.mode === "paint") {
      if (e.pointerId !== g.id) return;
      const evs = e.nativeEvent?.getCoalescedEvents?.() || [];
      for (const ev of evs.length ? evs : [e]) {
        const [x, y] = toPaintPx(ev);
        extendStroke(x, y, ev);
      }
      return;
    }
    if (!pts.current.has(e.pointerId)) return;
    const p = localPx(e);
    pts.current.set(e.pointerId, p);
    if (!g) return;
    const id = merch.selectedId;
    if (!id) return;
    if (!g.live) {
      if (g.mode === "move" && Math.hypot(p[0] - g.p0[0], p[1] - g.p0[1]) < (e.pointerType === "touch" ? 7 : 3)) return;
      g.live = true;
      beginLive(id);
    }

    if (g.mode === "move") {
      if (g.id !== id) return;
      updateTransform(id, { tx: g.tx + (p[0] - g.p0[0]) / k, ty: g.ty + (p[1] - g.p0[1]) / k });
    } else if (g.mode === "scale") {
      const f = Math.hypot(p[0] - g.c[0], p[1] - g.c[1]) / g.d0;
      updateTransform(id, scaledPatch(g.t0, f));
      setReadout(`${Math.round(f * 100)}%`);
    } else if (g.mode === "rotate") {
      const a = Math.atan2(p[1] - g.c[1], p[0] - g.c[0]);
      const deg = snapRot(g.r0 + (a - g.a0), e.shiftKey);
      updateTransform(id, { rot: (deg * Math.PI) / 180 });
      setReadout(`${Math.round(deg)}°`);
    } else if (g.mode === "pinch" && pts.current.size >= 2) {
      const [a, b] = [...pts.current.values()];
      const f = Math.hypot(b[0] - a[0], b[1] - a[1]) / g.d0;
      const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
      const m = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const deg = snapRot((g.t0.rot || 0) + (ang - g.a0), false);
      updateTransform(id, {
        ...scaledPatch(g.t0, f),
        rot: (deg * Math.PI) / 180,
        tx: (g.t0.tx ?? 0.5) + (m[0] - g.m0[0]) / k,
        ty: (g.t0.ty ?? 0.5) + (m[1] - g.m0[1]) / k,
      });
    }
  }

  function onPointerUp(e) {
    const g = gesture.current;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    cpts.current.delete(e.pointerId);
    if (g?.mode === "nav") {
      if (touchPts().length < 2) {
        gesture.current = null; // the finger still down does nothing until lifted
        pts.current.clear();
        endNav(g);
      }
      return;
    }
    if (g?.mode === "pan") {
      if (e.pointerId === g.id) {
        gesture.current = null;
        wrapRef.current?.classList.remove("panning");
        setScrollX(scrollRef.current?.scrollLeft || 0);
      }
      return;
    }
    if (g?.mode === "paint") {
      if (e.pointerId === g.id) { endStroke(); gesture.current = null; }
      return;
    }
    pts.current.delete(e.pointerId);
    // a tap (no drag) changes the selection
    if (g?.mode === "move" && !g.live && pts.current.size === 0 && g.tap !== merch.selectedId) {
      selectLayer(g.tap || null);
    }
    // lifting one finger of a pinch ends the gesture (avoids a jump)
    if (g?.mode === "pinch" || pts.current.size === 0) {
      gesture.current = null;
      endLive();
    }
    setReadout(null);
  }

  /* ---------- keyboard ---------- */
  useEffect(() => {
    function onKey(e) {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      if (merch.paint.active) return;
      // ⌘/Ctrl+Z undo, ⇧⌘Z / Ctrl+Y redo
      if ((e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z" || e.key === "y")) {
        e.preventDefault();
        if (e.key === "y" || e.shiftKey) redo(); else undo();
        return;
      }
      const id = merch.selectedId;
      if (!id) return;
      const L = merch.layers.find((l) => l.id === id);
      if (!L || L.locked) return;
      if ((e.metaKey || e.ctrlKey) && (e.key === "d" || e.key === "D")) {
        e.preventDefault();
        duplicateLayer(id);
        return;
      }
      const mm = e.shiftKey ? 1 : 0.2; // fine by default, Shift for bigger steps
      const du = (mm / sticker.widthMm) * aw;
      const t = L.transform;
      const moves = { ArrowLeft: [-du, 0], ArrowRight: [du, 0], ArrowUp: [0, -du], ArrowDown: [0, du] };
      if (moves[e.key]) {
        e.preventDefault();
        updateTransform(id, { tx: (t.tx ?? 0.5) + moves[e.key][0], ty: (t.ty ?? 0.5) + moves[e.key][1] });
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        removeLayer(id);
      } else if (e.key === "Escape") {
        selectLayer(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sticker, aw]);

  /* ---------- nudge buttons (hold to repeat) ---------- */
  const NUDGE_MM = 0.1;
  function nudge(kind, dir) {
    const id = merch.selectedId;
    const L = id && merch.layers.find((l) => l.id === id);
    if (!L) return;
    const t = L.transform;
    const du = (NUDGE_MM / sticker.widthMm) * aw;
    if (kind === "x") updateTransform(id, { tx: (t.tx ?? 0.5) + dir * du });
    else if (kind === "y") updateTransform(id, { ty: (t.ty ?? 0.5) + dir * du });
    else if (kind === "rot") updateTransform(id, { rot: (t.rot || 0) + (dir * 0.5 * Math.PI) / 180 });
    else if (kind === "scale") updateTransform(id, scaledPatch(t, dir > 0 ? 1.005 : 1 / 1.005));
  }
  const repeat = useRef(null);
  function holdStart(e, kind, dir) {
    e.preventDefault();
    nudge(kind, dir);
    clearTimeout(repeat.current?.t); clearInterval(repeat.current?.i);
    const t = setTimeout(() => { repeat.current.i = setInterval(() => nudge(kind, dir), 50); }, 350);
    repeat.current = { t };
  }
  function holdEnd() {
    clearTimeout(repeat.current?.t); clearInterval(repeat.current?.i);
    repeat.current = null;
  }
  useEffect(() => holdEnd, []);
  const nb = (kind, dir, label, aria) => (
    <button
      aria-label={aria}
      onPointerDown={(e) => holdStart(e, kind, dir)}
      onPointerUp={holdEnd} onPointerLeave={holdEnd} onPointerCancel={holdEnd}
      onContextMenu={(e) => e.preventDefault()}
    >{label}</button>
  );

  /* ---------- selection overlay ---------- */
  let overlay = null;
  const sel = snap.layers.find((l) => l.id === selectedId && !l.locked && l.visible !== false);
  const selDims = sel && dimsOf(sel.url);
  if (!paintActive && sel && selDims && view.W) {
    const t = sel.transform;
    const [hxU, hyU] = halfExtents(t, selDims);
    const hx = Math.max(hxU * k, 18);
    const hy = Math.max(hyU * k, 18);
    const [cx, cy] = unitToPx(t.tx ?? 0.5, t.ty ?? 0.5);
    const deg = ((t.rot || 0) * 180) / Math.PI;
    const corners = [[-hx, -hy], [hx, -hy], [hx, hy], [-hx, hy]];
    overlay = (
      <g transform={`translate(${cx} ${cy}) rotate(${deg})`}>
        <rect className="ma-sel-box" x={-hx} y={-hy} width={hx * 2} height={hy * 2} />
        <line className="ma-sel-box" x1={0} y1={-hy} x2={0} y2={-hy - KNOB_GAP} />
        {corners.map(([x, y], i) => (
          <g key={i}>
            <circle className="ma-handle" cx={x} cy={y} r={HANDLE_R} />
            <circle data-handle="scale" className="ma-hit ma-hit-scale" cx={x} cy={y} r={HIT_R} />
          </g>
        ))}
        <circle className="ma-handle ma-handle-rot" cx={0} cy={-hy - KNOB_GAP} r={HANDLE_R + 1} />
        <path className="ma-rot-icon" d={`M ${-3.5} ${-hy - KNOB_GAP - 1} a 3.5 3.5 0 1 1 1.5 3`} />
        <circle data-handle="rotate" className="ma-hit ma-hit-rot" cx={0} cy={-hy - KNOB_GAP} r={HIT_R} />
        {/* on top of the handle hit areas: touches inside the box always move */}
        <rect
          data-handle="body" className="ma-sel-body"
          x={-hx} y={-hy} width={hx * 2} height={hy * 2}
        />
        {readout && (
          <g transform={`rotate(${-deg}) translate(0 ${-Math.max(hx, hy) - KNOB_GAP - 26})`}>
            <rect className="ma-readout-bg" x={-24} y={-11} width={48} height={22} rx={11} />
            <text className="ma-readout" x={0} y={4} textAnchor="middle">{readout}</text>
          </g>
        )}
      </g>
    );
  }

  const scrolls = view.W > 0 && wrapRef.current && view.W > wrapRef.current.clientWidth - (fill ? 56 : 32) - 24 + 1;

  return (
    <div ref={wrapRef} className={`ma-print-editor ${fill ? "fill" : ""} ${scrolls ? "scrolls" : ""} ${zoom > 1 ? "zoomed" : ""}`}>
      <div className="ma-zoom" role="group" aria-label="Zoom">
        <button onClick={() => stepZoom(-1)} disabled={zoom <= 1} aria-label="Zoom out">−</button>
        <button className="ma-zoom-pct" onClick={() => zoomAt(1)} disabled={zoom <= 1} title="Fit (pinch with two fingers, or ⌘ + scroll, to zoom)" aria-label="Zoom to fit">{Math.round(zoom * 100)}%</button>
        <button onClick={() => stepZoom(1)} disabled={zoom >= ZMAX} aria-label="Zoom in">+</button>
        {!paintActive && (
          <>
            <button className="ma-undo-btn" onClick={undo} disabled={!hist.canUndo} aria-label="Undo" title="Undo (⌘Z)">↶</button>
            <button className="ma-undo-btn" onClick={redo} disabled={!hist.canRedo} aria-label="Redo" title="Redo (⇧⌘Z)">↷</button>
          </>
        )}
        {guides?.length > 0 && (
          <button
            className="ma-guides-btn"
            aria-pressed={showGuides}
            onClick={() => setShowGuides((v) => !v)}
            title="Show/hide the colouring guides (they never print)"
          >
            Guides
          </button>
        )}
        <button
          className="ma-guides-btn"
          aria-pressed={showSafe}
          onClick={() => setShowSafe((v) => !v)}
          title="Show/hide the blue safe edge (keep text inside it)"
        >
          Edge
        </button>
      </div>
     <div
       className="ma-scroll"
       ref={scrollRef}
       onScroll={(e) => {
         setScrollX(e.currentTarget.scrollLeft);
         if (zoomRef.current > 1 && !scrollRaf.current) {
           scrollRaf.current = requestAnimationFrame(() => { scrollRaf.current = 0; setViewTick((n) => n + 1); });
         }
       }}
       style={{ touchAction: "none", ...(zoom > 1 && view.baseH ? { maxHeight: view.baseH + 24 } : null) }}
       onPointerDown={onPointerDown}
       onPointerMove={onPointerMove}
       onPointerUp={onPointerUp}
       onPointerCancel={onPointerUp}
       onPointerEnter={() => { hovering.current = true; }}
       onPointerLeave={() => { hovering.current = false; }}
       onContextMenu={(e) => e.preventDefault()}
     >
      <div
        className="ma-canvas-wrap"
        ref={canvasWrapRef}
        style={{ cursor: paintActive ? "crosshair" : "default" }}
      >
        <canvas ref={canvasRef} />
        <svg className="ma-overlay" width={view.W} height={view.H}>
          {overlay}
        </svg>
      </div>
     </div>
      {scrolls && (
        <input
          className="ma-scrollbar"
          type="range"
          aria-label="Scroll along the sticker"
          min={0}
          max={Math.max(0, view.W - (scrollRef.current?.clientWidth || 0))}
          value={scrollX}
          onChange={(e) => {
            const v = Number(e.target.value);
            setScrollX(v);
            if (scrollRef.current) scrollRef.current.scrollLeft = v;
          }}
        />
      )}
      {!paintActive && sel && (
        <div className="ma-nudge" role="group" aria-label="Fine adjust">
          <span className="ma-nudge-set">
            {nb("x", -1, "←", "Nudge left")}
            {nb("y", -1, "↑", "Nudge up")}
            {nb("y", 1, "↓", "Nudge down")}
            {nb("x", 1, "→", "Nudge right")}
          </span>
          <span className="ma-nudge-set">
            {nb("rot", -1, "↺", "Rotate left 0.5°")}
            {nb("rot", 1, "↻", "Rotate right 0.5°")}
          </span>
          <span className="ma-nudge-set">
            {nb("scale", -1, "−", "Smaller")}
            {nb("scale", 1, "+", "Bigger")}
          </span>
        </div>
      )}
      {!fill && <div className="ma-legend">
        <span><i className="ma-key cut" /> Cut line</span>
        <span><i className="ma-key safe" /> Keep text out of the blue edge</span>
        <span>{sticker.widthMm} × {sticker.heightMm} mm</span>
      </div>}
    </div>
  );
}
